/**
 * Regras de apresentação da página de local, fora dos componentes para serem
 * testadas sem DOM. Nada aqui inventa conteúdo: os textos editoriais vêm da
 * API e os números saem das estatísticas calculadas no backend.
 */
import { Property } from '../../types';
import { LocationKind, LocationStats } from '../../types/location';
import { getDisplayPricing } from '../PropertyCard';
import { coordinatesOf } from '../property/propertyMap';
import type { MapPin } from '../property/propertyClusters';
import { toWhatsAppPhone } from '../../utils/whatsappPhone';

const KIND_LABELS: Record<LocationKind, string> = {
  condominio: 'Condomínio',
  edificio: 'Edifício',
  bairro: 'Bairro',
};

export const kindLabel = (kind: LocationKind): string => KIND_LABELS[kind] || 'Local';

const floorTo = (value: number, digits: number) => {
  const factor = 10 ** digits;
  return Math.floor(value * factor) / factor;
};

const number = (value: number, digits = 0) =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: digits }).format(value);

/** "R$ 1,4 mi", "R$ 850 mil", "R$ 7.000". Arredonda para baixo: é "a partir de". */
export const formatCompactBRL = (value: number): string => {
  if (value >= 1_000_000) return `R$ ${number(floorTo(value / 1_000_000, 2), 2)} mi`;
  if (value >= 100_000) return `R$ ${number(Math.floor(value / 1_000))} mil`;
  return `R$ ${number(Math.floor(value))}`;
};

const TYPE_NAMES: Record<string, [string, string]> = {
  casa: ['Casa', 'Casas'],
  apartamento: ['Apartamento', 'Apartamentos'],
  terreno: ['Terreno', 'Terrenos'],
  chacara: ['Chácara', 'Chácaras'],
  comercial: ['Imóvel comercial', 'Imóveis comerciais'],
};

const subjectFor = (stats: LocationStats, count: number): string => {
  const onlyType = stats.types.length === 1 ? TYPE_NAMES[stats.types[0].type] : undefined;
  const [singular, plural] = onlyType || ['Imóvel', 'Imóveis'];
  return count === 1 ? singular : plural;
};

const priceDetail = (min: number | null, max: number | null, suffix = ''): string => {
  if (min === null) return 'valor sob consulta';
  const formatted = `${formatCompactBRL(min)}${suffix}`;
  return max !== null && max > min ? `a partir de ${formatted}` : formatted;
};

const rangeText = (min: number | null, max: number | null, unit = ''): string | null => {
  if (min === null) return null;
  const text = max !== null && max > min ? `${number(min)} a ${number(max)}` : number(min);
  return `${text}${unit}`;
};

export interface StatCell {
  key: 'sale' | 'rental' | 'bedrooms' | 'area';
  label: string;
  value: string;
  detail: string;
}

export const buildStatCells = (stats: LocationStats): StatCell[] => {
  const cells: StatCell[] = [];
  const { sale, rental } = stats;
  if (sale.count > 0) {
    cells.push({
      key: 'sale',
      label: `${subjectFor(stats, sale.count)} à venda`,
      value: String(sale.count),
      detail: priceDetail(sale.min_price, sale.max_price),
    });
  }
  if (rental.count > 0) {
    cells.push({
      key: 'rental',
      label: `${subjectFor(stats, rental.count)} para alugar`,
      value: String(rental.count),
      detail: priceDetail(rental.min_price, rental.max_price, '/mês'),
    });
  }
  const bedrooms = rangeText(stats.bedrooms.min, stats.bedrooms.max);
  if (bedrooms) cells.push({ key: 'bedrooms', label: 'Quartos', value: bedrooms, detail: 'por imóvel' });
  const area = rangeText(stats.area.min, stats.area.max, ' m²');
  if (area) cells.push({ key: 'area', label: 'Área útil', value: area, detail: 'por imóvel' });
  return cells;
};

export const splitByPurpose = (properties: Property[]) => {
  const offers = (item: Property, businessType: 'SALE' | 'RENTAL') =>
    getDisplayPricing(item).some((pricing) => pricing.business_type === businessType);
  return {
    sale: properties.filter((item) => offers(item, 'SALE')),
    rental: properties.filter((item) => offers(item, 'RENTAL')),
  };
};

export const toParagraphs = (text?: string | null): string[] =>
  (text || '')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

const firstImage = (item: Property): string | undefined => {
  const first = (item.images || [])[0] as unknown;
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') return (first as { url?: string }).url;
  return undefined;
};

export const pinsFromProperties = (properties: Property[]): MapPin[] =>
  properties.flatMap((item) => {
    const point = coordinatesOf(item.address);
    if (!point) return [];
    return [{
      id: String(item.id),
      title: item.title,
      type: item.type,
      transaction_type: item.transaction_type,
      price: item.show_price === false ? undefined : item.price,
      cover_image: item.cover_image || firstImage(item),
      bedrooms: item.bedrooms,
      latitude: point[0],
      longitude: point[1],
    }];
  });

const dms = (value: number, positive: string, negative: string): string => {
  const absolute = Math.abs(value);
  let degrees = Math.floor(absolute);
  let minutes = Math.floor((absolute - degrees) * 60);
  let seconds = Math.round(((absolute - degrees) * 60 - minutes) * 60);
  if (seconds === 60) { seconds = 0; minutes += 1; }
  if (minutes === 60) { minutes = 0; degrees += 1; }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${degrees}°${pad(minutes)}′${pad(seconds)}″${value < 0 ? negative : positive}`;
};

/** Marca de agrimensor do local: "20°51′36″S · 49°19′31″O". */
export const formatCoordinates = (latitude: number, longitude: number): string =>
  `${dms(latitude, 'N', 'S')} · ${dms(longitude, 'L', 'O')}`;

export const whatsAppHref = (phone: string | null | undefined, message: string): string | null => {
  const digits = toWhatsAppPhone(phone);
  return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : null;
};

const TITLE_LIMIT = 65;

/** Mesmo critério do SEO do servidor: a variante mais completa que cabe. */
export const defaultTitle = (name: string, company: string): string => {
  const candidates = [
    `${name}: imóveis à venda e para alugar | ${company}`,
    `${name}: imóveis à venda e aluguel | ${company}`,
    `${name}: imóveis | ${company}`,
    `${name} | ${company}`,
  ];
  return candidates.find((title) => title.length <= TITLE_LIMIT) || candidates[candidates.length - 1];
};
